import { FetchResult } from '@apollo/client';
import { TFunction } from 'i18next';
import { AuthenticationConfirmationsResendEmailConfirmationMutation } from 'graphql-common';
import { FieldItem } from '@lib/interfaces/form';
import AvatarUploader from '@lib/components/AvatarUploader/AvatarUploader';
import UserFieldNames from '@lib/enums/fieldNames/userFieldNames';
import Input, { InputProps } from '@lib/components/Input/Input';
import { transformToUpperCase } from '@lib/utils/formHelpers';
import { InputTypes } from '@lib/components/Input/enums';
import {
  getEmailValidation,
  getFieldValidation,
  getPhoneValidation,
} from '@lib/utils/validationUtils';
import UnconfirmedEmailBlock from '../components/UnconfirmedEmailBlock';
import { OnUpdateProfile } from '../types';
import styles from '../components/ProfileForm.module.scss';

type ProfileFormFieldsArgs = {
  onResendConfirmationHandler: () => Promise<
    FetchResult<AuthenticationConfirmationsResendEmailConfirmationMutation>
  >;
  onSubmit: OnUpdateProfile;
  countriesCodes: string[];
  t: TFunction<'translation', undefined>;
  unconfirmedEmail?: string | null;
};

export default function getProfileFormFields({
  onResendConfirmationHandler,
  onSubmit,
  countriesCodes,
  t,
  unconfirmedEmail,
}: ProfileFormFieldsArgs): FieldItem[] {
  return [
    {
      name: 'avatarAttached',
      element: AvatarUploader,
      formItemClassName: styles.avatarBlock,
    },
    {
      name: UserFieldNames.FirstName,
      label: t('first-name'),
      element: Input,
      validation: getFieldValidation({ required: true, t }),
      formItemClassName: styles.column,
    },
    {
      name: UserFieldNames.LastName,
      label: t('last-name'),
      element: Input,
      validation: getFieldValidation({ required: true, t }),
      formItemClassName: styles.column,
      componentProps: {
        beforeValueChange: transformToUpperCase,
      },
    },
    {
      name: UserFieldNames.Email,
      label: t('work-email'),
      description: t('my-email-description'),
      element: Input,
      validation: getEmailValidation({ required: true, t }),
    },
    {
      name: UserFieldNames.UnconfirmedEmail,
      customComponent: unconfirmedEmail ? UnconfirmedEmailBlock : undefined,
      customComponentProps: {
        unconfirmedEmail,
        onResendConfirmationHandler,
        onSubmit,
      },
    },
    {
      name: UserFieldNames.PhoneNumber,
      label: t('phone-number'),
      element: Input,
      validation: getPhoneValidation({ required: false, t }),
      componentProps: {
        type: InputTypes.tel,
        onlyCountries: countriesCodes,
      } as InputProps,
      formItemClassName: styles.column,
    },
    {
      name: UserFieldNames.PersonalId,
      label: t('personal-id'),
      element: Input,
      validation: getFieldValidation({ t }),
      formItemClassName: styles.column,
    },
  ];
}
